import * as React from "react";
import { useEffect, useState, useMemo } from "react";
import { StyleSheet, View, FlatList } from "react-native";
import { useFormikContext } from "formik";

import {
  Checkbox,
  NumberInputField,
  Panel,
  Skeleton,
  Typography,
  VStack,
  HStack,
} from "@smartrent/ui";

import { useNetsuiteProductsQuery } from "@/api";
import { SelectedDevice, NetsuiteData } from "@/types";
import { formatCurrency } from "@/lib/helpers";
import Alert from "@/common/Alert";

import type { NetsuiteFormValues as FormValues } from "../types";

interface NetsuiteSelectDevicesFormProps {
  groupId: number | null;
}

const SUBTOTAL_LIMIT = 5000;

export const NetsuiteSelectDevicesForm: React.FC<
  NetsuiteSelectDevicesFormProps
> = ({ groupId }) => {
  const { setFieldValue } = useFormikContext<FormValues>();

  const [localDevices, setLocalDevices] = useState<SelectedDevice[]>([]);

  const { data, isLoading, isError } = useNetsuiteProductsQuery(groupId);

  // Calculate subtotal from selected devices
  const subtotal = useMemo(() => {
    return localDevices.reduce((sum, device) => {
      return device.isSelected && device.quantity > 0
        ? sum + device.price * device.quantity
        : sum;
    }, 0);
  }, [localDevices]);

  const isOverLimit = subtotal > SUBTOTAL_LIMIT;

  // Initialize devices from NetSuite products
  useEffect(() => {
    if (data?.orderProductItems) {
      const devices: SelectedDevice[] = data.orderProductItems.map(
        (product) => ({
          id: product.id.toString(),
          name: product.displayName ?? product.name,
          price: product.price,
          type: product.productCategory,
          isSelected: false,
          quantity: 0,
        })
      );

      setLocalDevices(devices);

      // Store NetSuite data for later use
      const netsuiteData: NetsuiteData = {
        customerId: data.customer.id,
        propertyId: data.property.id,
        customerName: data.customer.name,
        propertyName: data.property.name,
        poRequired: data.customer.poRequired,
        iotWarrantyActive: data.property.iotWarrantyActive,
        shippingAddress: {
          address1: data.property.address.address1,
          address2: data.property.address.address2,
          city: data.property.address.city,
          state: data.property.address.state,
          zip: data.property.address.zip,
        },
      };

      setFieldValue("netsuiteData", netsuiteData);
    }
  }, [data, setFieldValue]);

  // Sync local devices to form values whenever they change
  useEffect(() => {
    const selectedDevices = localDevices.filter(
      (device) => device.isSelected && device.quantity > 0
    );
    setFieldValue("devices", selectedDevices);
    setFieldValue("subtotal", subtotal);
    setFieldValue("isSubtotalOverLimit", isOverLimit);
  }, [localDevices, setFieldValue, subtotal, isOverLimit]);

  // Sync form quantity values back to localDevices
  const handleQuantityFormChange = (deviceId: string, quantity: number) => {
    const numQuantity = quantity || 0;
    setLocalDevices((prev) =>
      prev.map((device) =>
        device.id === deviceId
          ? {
              ...device,
              quantity: Math.max(0, numQuantity),
              isSelected: numQuantity > 0,
            }
          : device
      )
    );
  };

  const handleToggleDevice = (deviceId: string) => {
    setLocalDevices((prev) =>
      prev.map((device) =>
        device.id === deviceId
          ? {
              ...device,
              isSelected: !device.isSelected,
              quantity: !device.isSelected ? device.quantity || 1 : 0,
            }
          : device
      )
    );
  };

  if (!groupId) {
    return (
      <Panel>
        <View style={styles.messageContainer}>
          <Typography type="body">
            Please select a property to view available devices.
          </Typography>
        </View>
      </Panel>
    );
  }

  if (isLoading) {
    return (
      <Panel>
        <VStack spacing={16}>
          <Skeleton height={60} />
          <Skeleton height={60} />
          <Skeleton height={60} />
        </VStack>
      </Panel>
    );
  }

  if (isError) {
    return (
      <Panel>
        <View style={styles.messageContainer}>
          <Typography type="body">
            Error loading products. Please try again.
          </Typography>
        </View>
      </Panel>
    );
  }

  const isWarrantyActive = data?.property?.iotWarrantyActive !== false;

  if (localDevices.length === 0) {
    return (
      <Panel>
        {!isWarrantyActive && (
          <View style={styles.bannerContainer}>
            <Alert type="warning">
              <Typography color="textTitleInverse">
                This property has no active warranty.
              </Typography>
            </Alert>
          </View>
        )}

        <View style={styles.messageContainer}>
          <Typography type="body">
            No products available for this property.
          </Typography>
        </View>
      </Panel>
    );
  }

  return (
    <Panel>
      <View style={styles.header}>
        <Typography type="title3">Select Devices</Typography>
        <Typography type="caption">
          Choose the devices you want to order and specify quantities
        </Typography>
      </View>

      {!isWarrantyActive && (
        <View style={styles.bannerContainer}>
          <Alert type="warning">
            <Typography color="textTitleInverse">
              This property has no active warranty.
            </Typography>
          </Alert>
        </View>
      )}

      <FlatList
        data={localDevices}
        keyExtractor={(item) => item.id}
        renderItem={({ item }) => (
          <View style={styles.deviceRow}>
            <HStack justify="space-between" align="center">
              <VStack spacing={4} style={styles.deviceInfo}>
                <Checkbox
                  label={item.name}
                  name={`device_${item.id}`}
                  checked={item.isSelected}
                  onChangeChecked={() => handleToggleDevice(item.id)}
                />
                <Typography type="caption" style={styles.priceText}>
                  {formatCurrency(item.price)}
                </Typography>
              </VStack>
              <View style={styles.quantityInput}>
                <NumberInputField
                  label="Quantity"
                  decimal={false}
                  textInputProps={{
                    value: item.quantity,
                    onChangeText: (value?: number) => {
                      const numValue = value || 0;
                      handleQuantityFormChange(item.id, numValue);
                    },
                  }}
                />
              </View>
            </HStack>
          </View>
        )}
      />

      <View style={styles.subtotalContainer}>
        <HStack justify="space-between" align="center">
          <Typography type="body" weight="semibold">
            Subtotal:
          </Typography>
          <Typography
            variation="bold.title.three"
            weight="bold"
            color={isOverLimit ? "warning" : undefined}
          >
            {formatCurrency(subtotal)}
          </Typography>
        </HStack>

        {isOverLimit && (
          <View style={styles.errorAlert}>
            <Alert type="error">
              <Typography color="textTitleInverse">
                Order subtotal cannot exceed {formatCurrency(SUBTOTAL_LIMIT)}.
                Please reduce your selection to proceed.
              </Typography>
            </Alert>
          </View>
        )}
      </View>
    </Panel>
  );
};

const styles = StyleSheet.create({
  header: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
  },
  bannerContainer: {
    padding: 16,
  },
  messageContainer: {
    padding: 32,
    alignItems: "center",
  },
  deviceRow: {
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#f0f0f0",
  },
  deviceInfo: {
    flex: 1,
  },
  priceText: {
    marginLeft: 32,
  },
  quantityInput: {
    width: 200,
  },
  subtotalContainer: {
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: "#e0e0e0",
  },
  errorAlert: {
    marginTop: 12,
  },
});
